import React, { useEffect } from 'react';

import Form from '../../Shared/UI/Form/components/Form';
import Field from '../../Shared/UI/Field/components/Field';
import FormRow from '../../Shared/UI/FormRow/components/FormRow';
import Input from '../../Shared/UI/Input/components/Input';
import Select from '../../Shared/UI/Select/components/Select';
import FormActions from '../../Shared/UI/FormActions/components/FormActions';
import Button from '../../Shared/UI/Button/components/Button';
import ResponsiveContainer from '../../Shared/UI/ResponsiveContainer/components/ResponsiveContainer';

import { setCursorPositionInDOM } from '../../RichTextEditor';

import { useProfileState, useProfileActions } from './ProfileContext';
import ExperienceDescriptionRichTextEditor from './ExperienceDescriptionRichTextEditor';
import ExperienceDescriptionFormattingControls from './ExperienceDescriptionFormattingControls';

import { updateUserExperience } from './restApi';

import editorStyles from './ExperienceDescriptionRichTextEditor.module.scss';

const UpdateUserExperienceForm = () => {
  const profileState = useProfileState();
  const { onResponse, onError, updateUserExperienceFormTitle, updateUserExperienceFormBiopharmaGroupName, updateUserExperienceFormStartMonth, updateUserExperienceFormStartYear, updateUserExperienceFormEndMonth, updateUserExperienceFormEndYear, setUpdateUserExperienceEditorState, closeUpdateUserExperienceModal } = useProfileActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");

  const editor = profileState?.updateUserExperienceEditor;
  const documentManager = editor?.documentManager;
  const editorState = documentManager ? { documentManager } : null;

  useEffect(() => {
    if (documentManager) {
      setCursorPositionInDOM({
        editableContentId: editorStyles.experienceEditorContainer,
        isibuteDocument: documentManager.getCurrentDocument(),
      });
    }
  }, [documentManager?.historyIndex]);

  const handleSubmit = () => {
    const updateUserExperienceForm = {
      ...profileState?.updateUserExperienceForm,
      documentComponents: documentManager ? documentManager.getCurrentDocument().asJSON().documentComponents : undefined,
    };

    updateUserExperience({
      onResponse: (response) => {
        onResponse({
          ...response,
          updateUserExperienceEditor: null,
        });
      },
      onError,
      body: {
        authenticityToken,
        updateUserExperienceForm,
      },
    });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Field label="Title" id="title">
        <Input
          type="text"
          value={profileState?.updateUserExperienceForm?.title || ''}
          onChange={(e) => {
            updateUserExperienceFormTitle(e.target.value);
          }}
        />
      </Field>

      <Field label="Organization Name" id="biopharmaGroupName">
        <Input
          type="text"
          value={profileState?.updateUserExperienceForm?.biopharmaGroupName || ''}
          onChange={(e) => {
            updateUserExperienceFormBiopharmaGroupName(e.target.value);
          }}
        />
      </Field>

      <FormRow>
        <Field label="Start Month" id="startMonth">
          <Select
            value={profileState?.updateUserExperienceForm?.startMonth || ''}
            onChange={(e) => {
              updateUserExperienceFormStartMonth(e.target.value);
            }}
          >
            {(profileState?.userExperienceStartMonthOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>

        <Field label="Start Year" id="startYear">
          <Select
            value={profileState?.updateUserExperienceForm?.startYear || ''}
            onChange={(e) => {
              updateUserExperienceFormStartYear(e.target.value);
            }}
          >
            {(profileState?.userExperienceStartYearOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>
      </FormRow>

      <FormRow>
        <Field label="End Month" id="endMonth">
          <Select
            value={profileState?.updateUserExperienceForm?.endMonth || ''}
            onChange={(e) => {
              updateUserExperienceFormEndMonth(e.target.value);
            }}
          >
            {(profileState?.userExperienceEndMonthOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>

        <Field label="End Year" id="endYear">
          <Select
            value={profileState?.updateUserExperienceForm?.endYear || ''}
            onChange={(e) => {
              updateUserExperienceFormEndYear(e.target.value);
            }}
          >
            {(profileState?.userExperienceEndYearOptions || []).map((option) => (
              <option key={option.value} value={option.value}>{option.displayValue}</option>
            ))}
          </Select>
        </Field>
      </FormRow>

      <Field label="Description" id="description">
        {editorState ? (
          <div>
            <ExperienceDescriptionFormattingControls
              contentEditable={true}
              setState={setUpdateUserExperienceEditorState}
              activeStyleTypes={editor?.activeStyleTypes}
              activeFormat={editor?.activeFormat}
              activeHref={editor?.activeHref}
              editableContentId={editorStyles.experienceEditorContainer}
            />
            <ExperienceDescriptionRichTextEditor
              state={editorState}
              setState={setUpdateUserExperienceEditorState}
              contentEditable={true}
            />
          </div>
        ) : null}
      </Field>

      <ResponsiveContainer name="form-actions">
        <FormActions>
          <Button
            variant="secondary"
            onClick={() => {
              closeUpdateUserExperienceModal();
            }}
          >
            Cancel
          </Button>
          <Button type="submit">
            Save Your Experience
          </Button>
        </FormActions>
      </ResponsiveContainer>
    </Form>
  );
};

export default UpdateUserExperienceForm;
